import type { ApplyRowResult, ScanRow } from "../../../api/officeIdentityBackfill.service.js";

/**
 * Builds the two CSV files for the office identity backfill tool (DEV-2803).
 *
 * - `buildPlannedCsv` is the pre-apply file: every selected scan row, with the
 *   office's current (wiped) value and the snapshot's proposal side by side.
 *   Taken BEFORE anything is written, so a tab that dies mid-run still leaves
 *   a record of what the run was going to do.
 * - `buildRollbackCsv` is the authoritative post-apply file: only the rows the
 *   API actually wrote (status "applied"), each carrying the `previous` value
 *   the apply response captured. Re-uploading it into the tool's rollback
 *   section restores those values with mode "restore" — a null previous
 *   becomes an empty cell, which restore reads as "clear the field".
 */

export const PLANNED_HEADERS = ["officeId", "officeName", "field", "current", "proposed"] as const;

export const ROLLBACK_HEADERS = ["officeId", "field", "previous"] as const;

/**
 * Leading characters that make Excel and Sheets treat a cell as a formula.
 * Office names come from the database, i.e. are untrusted as far as this
 * export is concerned, so a triggering cell is prefixed to read as text.
 */
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Quotes a field only when it needs it, and doubles any embedded quote —
 * office names carry commas and quotes.
 *
 * A field starting with a formula trigger is prefixed with a single quote so
 * the spreadsheet reads it as text. Quoting alone does not prevent this: Excel
 * strips the quotes and evaluates what is inside.
 */
function csvField(value: string | null): string {
  let text = value === null ? "" : value;
  if (FORMULA_TRIGGERS.some(trigger => text.startsWith(trigger))) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** One row per office+field, in the order the scan returned them. */
export function buildPlannedCsv(rows: ScanRow[]): string {
  const lines = [PLANNED_HEADERS.join(",")];
  for (const row of rows) {
    lines.push(
      [row.officeId, row.officeName, row.field, row.current, row.proposed].map(csvField).join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/**
 * @param results Apply results, in the SAME ORDER as the rows they answer —
 *   position is the only reliable join.
 */
export function buildRollbackCsv(results: ApplyRowResult[]): string {
  const lines = [ROLLBACK_HEADERS.join(",")];
  for (const result of results) {
    // Only rows the API actually wrote can be rolled back. A null previous is
    // kept — as an empty cell — because restoring it means clearing the field.
    if (result.status !== "applied") {
      continue;
    }
    lines.push([result.officeId, result.field, result.previous].map(csvField).join(","));
  }
  return `${lines.join("\n")}\n`;
}

/** Shared `2026-08-12-10-20-30` filename stamp, so both files sort together. */
function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function plannedFilename(now: Date): string {
  return `office-identity-backfill-planned-${filenameStamp(now)}.csv`;
}

/**
 * Deliberately unlike `plannedFilename`: both files end up in the same
 * downloads folder, and uploading the planned file into the rollback section
 * is the mistake this name exists to prevent.
 */
export function rollbackFilename(now: Date): string {
  return `office-identity-backfill-rollback-${filenameStamp(now)}.csv`;
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Builds the planned-changes CSV and hands it to the browser.
 *
 * @param at Stamps the filename — pass the moment of the download, so two
 *   downloads in one session cannot produce identically named files.
 */
export function downloadPlannedCsv(rows: ScanRow[], at: Date): void {
  downloadCsv(buildPlannedCsv(rows), plannedFilename(at));
}

/** Builds the authoritative post-apply rollback CSV and hands it to the browser. */
export function downloadRollbackCsv(results: ApplyRowResult[], at: Date): void {
  downloadCsv(buildRollbackCsv(results), rollbackFilename(at));
}
